import Icon from 'components/Icon';
import { ICONS } from 'consts/consts';
import React, { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Card, CardBody } from 'reactstrap';
import RichTextEditor from 'views/Widgets/Inputs/RichTextEditor';

interface Props {
  name: string;
  value?: string[];
  onChange: (items: string[]) => void;
  disabled?: boolean;
}

const AgendaItemsTemplateInput: FC<Props> = ({
  name,
  value,
  onChange,
  disabled = false,
}) => {
  const { formatMessage } = useIntl();
  const items = value ?? [];

  const updateItem = useCallback(
    (index: number, model: string) => {
      const updated = [...items];
      updated[index] = model;
      onChange(updated);
    },
    [items, onChange]
  );

  const removeItem = useCallback(
    (index: number) => {
      onChange(items.filter((_item, i) => i !== index));
    },
    [items, onChange]
  );

  const addItem = useCallback(() => {
    onChange([...items, '']);
  }, [items, onChange]);

  return (
    <div>
      {items.length === 0 && (
        <p className="text-muted small">
          <FormattedMessage
            id="app.views.administration.content_administration.templates.agenda_items.empty"
            defaultMessage="Each agenda item becomes a separate item when this template is added to a 1:1."
          />
        </p>
      )}
      {items.map((item, index) => (
        <Card key={`${name}-${index}`} className="mb-3">
          <CardBody className="p-3">
            <div className="d-flex align-items-start">
              <div className="flex-grow-1">
                <RichTextEditor
                  name={`${name}_${index}`}
                  inForm={true}
                  value={item}
                  minRows={2}
                  placeholder={formatMessage({
                    id: 'app.views.administration.content_administration.templates.agenda_items.placeholder',
                    defaultMessage: 'Agenda item',
                  })}
                  onChange={(model: string) => updateItem(index, model)}
                />
              </div>
              <Button
                color="light"
                size="sm"
                className="ms-2"
                disabled={disabled}
                onClick={() => removeItem(index)}
                aria-label={formatMessage({
                  id: 'app.views.administration.content_administration.templates.agenda_items.remove',
                  defaultMessage: 'Remove agenda item',
                })}
              >
                <Icon icon={ICONS.TRASH} />
              </Button>
            </div>
          </CardBody>
        </Card>
      ))}
      <Button color="light" size="sm" disabled={disabled} onClick={addItem}>
        <Icon icon={ICONS.ADD} className="me-1" />
        <FormattedMessage
          id="app.views.administration.content_administration.templates.agenda_items.add"
          defaultMessage="Add agenda item"
        />
      </Button>
    </div>
  );
};

export default React.memo(AgendaItemsTemplateInput);
